import { PaymentAction } from '@adyen/adyen-web/dist/types/types';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { Alert, LoaderSvg, LoaderSvgBitiba } from '@zooplus/ui-components';

import ActionResultsPortal from '@components/ActionResultsPortal/ActionResultsPortal';
import useActionCreator, { ACTION_CREATOR_CONTAINER, Details } from '@hooks/useActionCreator';
import { useTranslation } from '@hooks/useTranslations';
import { PaymentMethod, ShopBundle } from '@types-internal';
import { AdyenConfig } from '@types-internal/AdyenTypes';

import './ActionCreator.modules.scss';

export interface ActionCreatorProps extends AdyenConfig {
  paymentAction: PaymentAction;
  locale: string;
  csrfToken?: string;
  shopBundle?: ShopBundle;
  paymentId?: string;
  paymentMethod?: PaymentMethod;
  sendPaymentDetailsHandler?: (details: Details) => void;
  actionTimedOutHandler?: () => void;
  invalidQRCodeActionShopUrl?: string;
  onRetryExhausted?: () => void;
  isMocked?: boolean;
}

const ActionCreator: React.FC<ActionCreatorProps> = ({
  paymentAction,
  clientKey,
  adyenEnvironment,
  locale,
  sendPaymentDetailsHandler,
  shopBundle = 'zooplus',
  actionTimedOutHandler,
  invalidQRCodeActionShopUrl,
  paymentId,
  onRetryExhausted,
  isMocked = false,
}) => {
  const { t } = useTranslation();
  const [createPortal, setCreatePortal] = useState(false);
  const [showLoader, setShowLoader] = useState(true);
  const { details, isActionTimedOut } = useActionCreator(
    paymentAction,
    {
      clientKey,
      adyenEnvironment,
      locale,
      paymentId,
      onRetryExhausted,
    },
    isMocked,
  );

  useEffect(() => {
    // Observes when adyen-checkout__qr-loader appears in the DOM, then creates the portal and shows the loader

    /* Suggestion to refactor: This might also be detected using the handler "onActionHandled()" from the Adyen SDK,
     * by passing the handler to the AdyenCheckout object config. This handler will receive the action description
     * type 'qr-code-loaded' | 'polling-started' | 'fingerprint-iframe-loaded' | 'challenge-iframe-loaded'. */

    const isQrLoaderNode = (node: Node) =>
      (node as Element).classList.contains('adyen-checkout__qr-loader');

    const observer = new MutationObserver(mutationsList => {
      const qrLoaderAdded = mutationsList.some(mutation =>
        Array.from(mutation.addedNodes).some(isQrLoaderNode),
      );
      if (qrLoaderAdded) {
        setCreatePortal(true);
        setShowLoader(false);
        observer.disconnect();
      }
    });
    observer.observe(document.querySelector(`#${ACTION_CREATOR_CONTAINER}`) as Element, {
      subtree: false,
      childList: true,
    });
  }, []);

  useEffect(() => {
    if (!!details && sendPaymentDetailsHandler) {
      sendPaymentDetailsHandler(details);
    }
  }, [details, sendPaymentDetailsHandler]);

  useEffect(() => {
    if (isActionTimedOut && actionTimedOutHandler) {
      actionTimedOutHandler();
    }
  }, [isActionTimedOut, actionTimedOutHandler]);

  if (paymentAction.type === 'redirect') {
    return <div data-zta={ACTION_CREATOR_CONTAINER} id={ACTION_CREATOR_CONTAINER} />;
  }

  return (
    <>
      {/* Div where Adyen loads its div to show challenge and QR code */}
      <div data-zta={ACTION_CREATOR_CONTAINER} id={ACTION_CREATOR_CONTAINER}>
        {/* Not showing the loader in case of 3DS or await, The Adyen 3DS challenge and await UI already create a spinner for it. */}
        {!['threeDS2', 'await'].includes(paymentAction.type) && showLoader && (
          <div className="payments-loader">
            {shopBundle === 'zooplus' ? <LoaderSvg /> : <LoaderSvgBitiba />}
          </div>
        )}
      </div>

      {createPortal && !details && (
        <ActionResultsPortal>
          <Alert variant="warning">
            <Trans
              t={t}
              i18nKey="checkout.payment.authorization.invalid"
              components={{ url: <a href={invalidQRCodeActionShopUrl}>here</a> }}
            />
          </Alert>
        </ActionResultsPortal>
      )}

      {createPortal && details && (
        <ActionResultsPortal>
          <Alert variant="info">{t('checkout.payment.authorization.processing')}</Alert>
        </ActionResultsPortal>
      )}
    </>
  );
};

export default ActionCreator;
